import { useRestockIngredient } from "@/services/ingredients/mutation";
import { Ingredient } from "@/types/ingredient";
import { Alert, Button, Modal, Form, InputNumber, Space, message, Descriptions } from "antd";
import { forwardRef, useImperativeHandle, useState } from "react";

export interface ModalRestockIngredientRef {
  childFunction: (ingredient: Ingredient) => void;
}

export const ModalRestockIngredient = forwardRef<ModalRestockIngredientRef>((props, ref) => {
  const [open, setOpen] = useState<boolean>(false);
  const [ingredient, setIngredient] = useState<Ingredient | null>(null);

  const [form] = Form.useForm();
  const quantityInput = Form.useWatch("quantity", form);

  const [messageApi, contextHolder] = message.useMessage();
  const restockMutation = useRestockIngredient();

  const childFunction = (selectedIngredient: Ingredient) => {
    setIngredient(selectedIngredient);
    form.resetFields();
    form.setFieldsValue({ quantity: 1 });
    setOpen(true);
  };

  useImperativeHandle(ref, () => ({
    childFunction,
  }));

  const onFinish = (values: any) => {
    if (!ingredient) return;
    const quantity = Number(values.quantity);
    if (!quantity || quantity <= 0) {
      messageApi.error("Por favor, ingresa una cantidad válida mayor a 0.");
      return;
    }

    restockMutation.mutate(
      {
        ingredientId: ingredient.id,
        quantity,
      },
      {
        onSuccess: () => {
          messageApi.success("Stock del ingrediente reabastecido con éxito.");
          setOpen(false);
        },
        onError: (err: any) => {
          messageApi.error(
            err?.response?.data?.message || "Error al reabastecer el stock."
          );
        },
      }
    );
  };

  const getUnitLabel = (unit?: string) => {
    if (!unit) return "";
    const unitLabels: Record<string, string> = {
      g: "Gramos (g)",
      kg: "Kilogramos (kg)",
      ml: "Mililitros (ml)",
      l: "Litros (L)",
      unit: "Unidades",
      bottle: "Botellas",
      can: "Latas",
    };
    return unitLabels[unit] || unit;
  };

  const currentStock = ingredient ? Number(ingredient.stock) : 0;
  const qtyToAdd = Number(quantityInput) || 0;
  const estimatedStock = currentStock + qtyToAdd;
  const isSubmitting = restockMutation.isPending;

  return (
    <Modal
      title={ingredient ? `Aumentar stock de ingrediente: ${ingredient.name}` : "Aumentar stock"}
      centered
      open={open}
      width={600}
      onCancel={() => setOpen(false)}
      footer={
        <Space>
          <Button onClick={() => setOpen(false)} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            type="primary"
            onClick={() => form.submit()}
            loading={isSubmitting}
          >
            Guardar
          </Button>
        </Space>
      }
    >
      {contextHolder}

      <div className="mt-4 space-y-6">
        {ingredient && (
          <Descriptions bordered column={1} size="small" className="mb-4">
            <Descriptions.Item label="Ingrediente">
              <span className="font-semibold">{ingredient.name}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Unidad de medida">
              {getUnitLabel(ingredient.unit)}
            </Descriptions.Item>
            <Descriptions.Item label="Stock actual">
              <span className="font-mono font-semibold">
                {currentStock.toLocaleString("es-CL", { maximumFractionDigits: 3 })}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Stock final estimado">
              <span className="font-mono font-bold text-primary">
                {estimatedStock.toLocaleString("es-CL", { maximumFractionDigits: 3 })}
              </span>
            </Descriptions.Item>
          </Descriptions>
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{ quantity: 1 }}
        >
          <Form.Item
            name="quantity"
            label="Cantidad a sumar al stock"
            rules={[
              { required: true, message: "Ingresa la cantidad" },
              { type: "number", min: 0.001, message: "La cantidad debe ser mayor a 0" },
            ]}
          >
            <InputNumber
              min={0.001}
              placeholder="Ingresa la cantidad a reabastecer"
              style={{ width: "100%" }}
              size="large"
            />
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
});
